"use client";

import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  ChevronDown,
  Code2,
  FileText,
  Info,
  LayoutTemplate,
  RotateCcw,
  Workflow,
  X,
} from "lucide-react";
import {
  defaultPdfOptions,
  type MarginPreset,
  type Orientation,
  type PaperSize,
  type PdfCodeTheme,
  type PdfDiagramTheme,
  type PdfOptions,
  type PdfTheme,
  PDF_CODE_THEMES,
  PDF_THEMES,
} from "@/src/lib/pdf/print-document";
import type { PdfFontKey } from "@/src/lib/pdf/fonts";
import {
  DEFAULT_EDITOR_FONT_FAMILY,
  DEFAULT_EDITOR_FONT_SIZE,
} from "@/src/lib/storage/user-preferences";
import { classNames } from "../ui/class-names";
import { SegmentedControl } from "../ui/segmented-control";
import { OptionToggle } from "../ui/option-toggle";
import { FontAndSizeControl } from "./font-and-size-control";
import {
  codeThemeLabels,
  diagramThemeLabels,
  fontLabels,
  marginSummaryLabels,
  orientationLabels,
  paperSizeLabels,
  themeLabels,
} from "./labels";

const autoThemeHelper = "Matches PDF theme.";

type SettingsTab = "export" | "editor";

export type PreviewOptionsMenuProps = Readonly<{
  pdfOptions: PdfOptions;
  onPdfOptionsChange: (options: PdfOptions) => void;
  editorFontFamily: PdfFontKey;
  editorFontSize: number;
  onEditorFontFamilyChange: (fontFamily: PdfFontKey) => void;
  onEditorFontSizeChange: (fontSize: number) => void;
  onClose?: () => void;
}>;

function SettingsCard({
  title,
  icon,
  summary,
  defaultOpen = false,
  children,
}: Readonly<{
  title: string;
  icon: ReactNode;
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}>) {
  const [open, setOpen] = useState(defaultOpen);
  const headerTextRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [titleOffsetY, setTitleOffsetY] = useState(0);

  useLayoutEffect(() => {
    const headerText = headerTextRef.current;
    const titleEl = titleRef.current;
    if (!headerText || !titleEl) {
      return;
    }

    const measure = () => {
      const nextOffset = Math.max(
        0,
        (headerText.clientHeight - titleEl.offsetHeight) / 2,
      );
      setTitleOffsetY(nextOffset);
    };

    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(headerText);
    observer.observe(titleEl);
    return () => observer.disconnect();
  }, [summary, title]);

  return (
    <details
      open
      data-expanded={open ? "true" : "false"}
      className="group rounded-lg border border-zinc-200 bg-white"
    >
      <summary
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault();
          setOpen((current) => !current);
        }}
        className="flex cursor-pointer list-none items-center gap-3.5 rounded-lg px-3.5 py-3 outline-brand marker:content-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 [&::-webkit-details-marker]:hidden"
      >
        <span
          aria-hidden="true"
          className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand"
        >
          {icon}
        </span>
        <span ref={headerTextRef} className="relative min-w-0 flex-1">
          <span
            className="block"
            style={{ visibility: "hidden" }}
            aria-hidden="true"
          >
            <span className="block text-sm font-semibold text-zinc-950">
              {title}
            </span>
            {summary ? (
              <span className="mt-1.5 block text-sm font-normal leading-snug text-zinc-500">
                {summary}
              </span>
            ) : null}
          </span>
          <span className="absolute inset-0 overflow-hidden">
            <span
              ref={titleRef}
              className="absolute inset-x-0 top-0 text-sm font-semibold text-zinc-950 will-change-transform"
              style={{
                transform: open
                  ? `translate3d(0, ${titleOffsetY}px, 0)`
                  : "translate3d(0, 0, 0)",
                transition:
                  "transform 450ms cubic-bezier(0.22, 1, 0.36, 1)",
              }}
            >
              {title}
            </span>
            {summary ? (
              <span
                className="absolute inset-x-0 text-sm font-normal leading-snug text-zinc-500"
                style={{
                  top: "calc(1.25rem + 0.375rem)",
                  opacity: open ? 0 : 1,
                  transition: "opacity 200ms ease-out",
                  transitionDelay: open ? "0ms" : "100ms",
                }}
                aria-hidden={open || undefined}
              >
                {summary}
              </span>
            ) : null}
          </span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={classNames(
            "size-4 shrink-0 text-zinc-600 transition-transform duration-300 ease-out",
            open && "rotate-180",
          )}
        />
      </summary>
      <div
        className={classNames(
          "grid transition-[grid-template-rows] duration-300 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div
          className="min-h-0 overflow-hidden"
          aria-hidden={!open}
          inert={!open ? true : undefined}
        >
          <div className="border-t border-zinc-100 px-3.5 py-3.5">
            {children}
          </div>
        </div>
      </div>
    </details>
  );
}

function FieldLabel({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <span className="text-sm font-medium text-zinc-700">{children}</span>
  );
}

function HelpText({
  id,
  children,
}: Readonly<{
  id?: string;
  children: ReactNode;
}>) {
  return (
    <p id={id} className="text-xs font-normal leading-snug text-zinc-500">
      {children}
    </p>
  );
}

function formatFontSize(size: number): string {
  return `${size} pt`;
}

export function PreviewOptionsMenu({
  pdfOptions,
  onPdfOptionsChange,
  editorFontFamily,
  editorFontSize,
  onEditorFontFamilyChange,
  onEditorFontSizeChange,
  onClose,
}: PreviewOptionsMenuProps) {
  const titleId = useId();
  const codeThemeHelperId = useId();
  const diagramThemeHelperId = useId();
  const [tab, setTab] = useState<SettingsTab>("export");

  function updateOption<K extends keyof PdfOptions>(
    key: K,
    value: PdfOptions[K],
  ) {
    onPdfOptionsChange({ ...pdfOptions, [key]: value });
  }

  function handleThemeChange(theme: PdfTheme) {
    onPdfOptionsChange({
      ...pdfOptions,
      theme,
      codeTheme: "auto",
      diagramTheme: "auto",
    });
  }

  function handleReset() {
    onPdfOptionsChange(defaultPdfOptions);
    onEditorFontFamilyChange(DEFAULT_EDITOR_FONT_FAMILY);
    onEditorFontSizeChange(DEFAULT_EDITOR_FONT_SIZE);
  }

  const pageLayoutSummary = [
    paperSizeLabels[pdfOptions.paperSize],
    orientationLabels[pdfOptions.orientation],
    marginSummaryLabels[pdfOptions.margins],
  ].join(" · ");

  const baseAppearanceSummary = [
    themeLabels[pdfOptions.theme],
    fontLabels[pdfOptions.fontFamily],
    formatFontSize(pdfOptions.fontSize),
  ].join(" · ");

  const codeSummary = [
    codeThemeLabels[pdfOptions.codeTheme],
    `${fontLabels[pdfOptions.codeFontFamily]} ${formatFontSize(pdfOptions.codeFontSize)}`,
  ].join(" · ");

  const diagramsSummary = [
    diagramThemeLabels[pdfOptions.diagramTheme],
    `${fontLabels[pdfOptions.diagramFontFamily]} ${formatFontSize(pdfOptions.diagramFontSize)}`,
  ].join(" · ");

  const editorAppearanceSummary = [
    fontLabels[editorFontFamily],
    formatFontSize(editorFontSize),
  ].join(" · ");

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="flex min-h-0 w-full flex-1 flex-col overflow-hidden"
    >
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-zinc-200 px-4 pb-3 pt-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-base font-semibold text-zinc-950">
            Document settings
          </h2>
          <p className="mt-0.5 text-xs font-normal text-zinc-500">
            Customize your PDF and editor appearance.
          </p>
        </div>
        {onClose ? (
          <button
            type="button"
            aria-label="Close document settings"
            onClick={onClose}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label="Settings categories"
        className="flex shrink-0 justify-center gap-24 border-b border-zinc-200 px-4 pt-1"
      >
        {(
          [
            { id: "export", label: "Export" },
            { id: "editor", label: "Editor" },
          ] as const
        ).map((item) => {
          const selected = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={selected}
              id={`settings-tab-${item.id}`}
              aria-controls={`settings-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setTab(item.id)}
              className={classNames(
                "-mb-px border-b-2 px-3 py-3 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                selected
                  ? "border-brand font-semibold text-zinc-950"
                  : "border-transparent font-medium text-zinc-500 hover:text-zinc-800",
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div
        data-testid="settings-menu-scroll"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4"
      >
        {tab === "export" ? (
          <div
            role="tabpanel"
            id="settings-panel-export"
            aria-labelledby="settings-tab-export"
            className="flex flex-col gap-4"
          >
            <SettingsCard
              title="Page & layout"
              icon={<LayoutTemplate className="size-4.5" />}
              summary={pageLayoutSummary}
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <FieldLabel>Page size</FieldLabel>
                  <SegmentedControl
                    label="Page size"
                    value={pdfOptions.paperSize}
                    options={[
                      { value: "a4", label: paperSizeLabels.a4 },
                      { value: "letter", label: paperSizeLabels.letter },
                    ]}
                    onChange={(value) =>
                      updateOption("paperSize", value as PaperSize)
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <FieldLabel>Orientation</FieldLabel>
                  <SegmentedControl
                    label="Orientation"
                    value={pdfOptions.orientation}
                    options={[
                      {
                        value: "portrait",
                        label: orientationLabels.portrait,
                      },
                      {
                        value: "landscape",
                        label: orientationLabels.landscape,
                      },
                    ]}
                    onChange={(value) =>
                      updateOption("orientation", value as Orientation)
                    }
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <FieldLabel>Margins</FieldLabel>
                  <SegmentedControl
                    label="Margins"
                    value={pdfOptions.margins}
                    options={[
                      { value: "narrow", label: "Narrow" },
                      { value: "normal", label: "Normal" },
                      { value: "wide", label: "Wide" },
                    ]}
                    onChange={(value) =>
                      updateOption("margins", value as MarginPreset)
                    }
                  />
                </div>
                <OptionToggle
                  label="Page numbers"
                  description="Show page numbers in exported PDF"
                  checked={pdfOptions.pageNumbers}
                  onChange={(checked) => updateOption("pageNumbers", checked)}
                />
              </div>
            </SettingsCard>

            <SettingsCard
              title="Document"
              icon={<FileText className="size-4.5" />}
              summary={baseAppearanceSummary}
            >
              <FontAndSizeControl
                label="PDF"
                idPrefix="preview-font"
                fontFamily={pdfOptions.fontFamily}
                fontSize={pdfOptions.fontSize}
                onFontFamilyChange={(value) =>
                  updateOption("fontFamily", value)
                }
                onFontSizeChange={(value) => updateOption("fontSize", value)}
                theme={{
                  value: pdfOptions.theme,
                  options: PDF_THEMES.map((theme) => ({
                    value: theme.id,
                    label: theme.label,
                  })),
                  onChange: handleThemeChange,
                  idPrefix: "doc-theme",
                  "aria-label": "PDF theme",
                }}
              />
            </SettingsCard>

            <SettingsCard
              title="Code"
              icon={<Code2 className="size-4.5" />}
              summary={codeSummary}
            >
              <div className="flex flex-col gap-1.5">
                <FontAndSizeControl
                  label="Code"
                  idPrefix="code-font"
                  fontFamily={pdfOptions.codeFontFamily}
                  fontSize={pdfOptions.codeFontSize}
                  onFontFamilyChange={(value) =>
                    updateOption("codeFontFamily", value)
                  }
                  onFontSizeChange={(value) =>
                    updateOption("codeFontSize", value)
                  }
                  theme={{
                    value: pdfOptions.codeTheme,
                    options: [
                      {
                        value: "auto" as const,
                        label: codeThemeLabels.auto,
                      },
                      ...PDF_CODE_THEMES.map((theme) => ({
                        value: theme.id,
                        label: theme.label,
                      })),
                    ],
                    onChange: (value) =>
                      updateOption("codeTheme", value as PdfCodeTheme),
                    idPrefix: "code-theme",
                    "aria-label": "Code theme",
                    "aria-describedby":
                      pdfOptions.codeTheme === "auto"
                        ? codeThemeHelperId
                        : undefined,
                  }}
                />
                {pdfOptions.codeTheme === "auto" ? (
                  <HelpText id={codeThemeHelperId}>{autoThemeHelper}</HelpText>
                ) : null}
              </div>
            </SettingsCard>

            <SettingsCard
              title="Diagrams"
              icon={<Workflow className="size-4.5" />}
              summary={diagramsSummary}
            >
              <div className="flex flex-col gap-1.5">
                <FontAndSizeControl
                  label="Diagram"
                  idPrefix="diagram-font"
                  fontFamily={pdfOptions.diagramFontFamily}
                  fontSize={pdfOptions.diagramFontSize}
                  onFontFamilyChange={(value) =>
                    updateOption("diagramFontFamily", value)
                  }
                  onFontSizeChange={(value) =>
                    updateOption("diagramFontSize", value)
                  }
                  theme={{
                    value: pdfOptions.diagramTheme,
                    options: [
                      {
                        value: "auto" as const,
                        label: diagramThemeLabels.auto,
                      },
                      ...PDF_THEMES.map((theme) => ({
                        value: theme.id,
                        label: theme.label,
                      })),
                    ],
                    onChange: (value) =>
                      updateOption("diagramTheme", value as PdfDiagramTheme),
                    idPrefix: "diagram-theme",
                    "aria-label": "Diagram theme",
                    "aria-describedby":
                      pdfOptions.diagramTheme === "auto"
                        ? diagramThemeHelperId
                        : undefined,
                  }}
                />
                {pdfOptions.diagramTheme === "auto" ? (
                  <HelpText id={diagramThemeHelperId}>
                    {autoThemeHelper}
                  </HelpText>
                ) : null}
              </div>
            </SettingsCard>
          </div>
        ) : (
          <div
            role="tabpanel"
            id="settings-panel-editor"
            aria-labelledby="settings-tab-editor"
            className="flex flex-col gap-4"
          >
            <SettingsCard
              title="Editor appearance"
              icon={<FileText className="size-4.5" />}
              summary={editorAppearanceSummary}
            >
              <FontAndSizeControl
                label="Editor"
                idPrefix="editor-font"
                fontFamily={editorFontFamily}
                fontSize={editorFontSize}
                onFontFamilyChange={onEditorFontFamilyChange}
                onFontSizeChange={onEditorFontSizeChange}
              />
            </SettingsCard>
            <p className="flex items-start gap-2 text-xs font-normal leading-snug text-zinc-500">
              <Info
                aria-hidden="true"
                className="mt-0.5 size-3.5 shrink-0 text-brand"
              />
              <span>Does not affect preview or downloaded PDFs.</span>
            </p>
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-zinc-200 px-4 py-3">
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 transition hover:text-zinc-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <RotateCcw aria-hidden="true" className="size-3.5" />
          Reset settings
        </button>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center rounded-md bg-brand px-4 text-sm font-semibold text-white transition hover:bg-brand-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            Close
          </button>
        ) : null}
      </div>
    </div>
  );
}
